import { ExtractComponentProp } from '@approvalmax/types';
import { Attachment, Box, Dropzone, Grid, openSplitViewState } from '@approvalmax/ui/src/components';
import { useAtomValue, useSetAtom } from 'jotai';
import { domain } from 'modules/data';
import { useDispatch } from 'modules/react-redux';
import { memo, useCallback, useMemo } from 'react';
import { splitViewGoToBarDefaultSettings, splitViewGoToBarSettingsState } from 'shared/components';
import { ContentSplitViewMode, contentSplitViewState, ocrIsLoadingState } from 'shared/states';

import { uploadAttachments } from '../../actions';
import { RequestEditMode } from '../../selectors/requestSelectors';
import { ocrAttachmentIdState } from '../SplitViewWrapper/components';
import { AttachmentListProps } from './AttachmentList.types';

export const AttachmentList = memo<AttachmentListProps>((props) => {
    const {
        className,
        attachments,
        newAttachments,
        isReadonly,
        attachmentKind,
        editMode,
        maxFilesCount,
        size,
        onRemoveAttachment,
        customHandleClickOnAttachment,
    } = props;

    const setOpenSplitView = useSetAtom(openSplitViewState);
    const setContentSplitView = useSetAtom(contentSplitViewState);
    const ocrAttachmentId = useAtomValue(ocrAttachmentIdState);
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);

    const dispatch = useDispatch();

    const open = useCallback(
        (attachment: domain.RequestAttachment) => {
            if (customHandleClickOnAttachment) {
                customHandleClickOnAttachment(attachment.id);
            } else {
                setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
                setOpenSplitView(true);
                setContentSplitView({
                    mode: ContentSplitViewMode.AttachmentsToRequest,
                    attachmentId: attachment.id,
                });
            }
        },
        [customHandleClickOnAttachment, setSplitViewGoToBarSettings, setOpenSplitView, setContentSplitView]
    );

    const handleDropAccepted = useCallback<ExtractComponentProp<typeof Dropzone, 'onDropAccepted'>>(
        (files) => {
            dispatch(uploadAttachments({ files: files.map((file) => file.source), attachmentKind }));
        },
        [dispatch, attachmentKind]
    );

    const attachmentsAsFiles = useMemo(
        () =>
            attachments.map((attachment) => {
                const file = new File([''], attachment.name);

                return {
                    ...attachment,
                    file,
                };
            }),
        [attachments]
    );

    const newAttachmentsAsFiles = useMemo(
        () =>
            newAttachments.map((attachment) => {
                const file = new File([''], attachment.name);

                return {
                    ...attachment,
                    file,
                };
            }),
        [newAttachments]
    );

    const allAttachmentsCount = attachments.length + newAttachments.length;

    const isCountLimitExceeded = typeof maxFilesCount === 'number' ? allAttachmentsCount >= maxFilesCount : false;

    const isEditorOrApprover = editMode === RequestEditMode.Editor || editMode === RequestEditMode.Approver;

    return (
        <div className={className}>
            <Grid rowGap={4}>
                {attachmentsAsFiles.map((attachment) => (
                    <Attachment
                        key={attachment.id}
                        id={attachment.id}
                        source={attachment.file}
                        onRemove={() => {
                            onRemoveAttachment(attachment.id);
                        }}
                        disabled={
                            isReadonly ||
                            (ocrAttachmentId === attachment.id && ocrIsLoading) ||
                            (!attachment.isNew && isEditorOrApprover)
                        }
                        progress={ocrAttachmentId === attachment.id && ocrIsLoading}
                        size={size}
                        onShow={() => open(attachment)}
                    />
                ))}

                {newAttachmentsAsFiles.map((attachment) => (
                    <Attachment key={attachment.id} id={attachment.id} source={attachment.file} progress size={size} />
                ))}
            </Grid>

            {!isReadonly && !isCountLimitExceeded && (
                <Box spacing='8 0 0 0'>
                    <Dropzone
                        size='small'
                        hideZone={allAttachmentsCount > 0}
                        onDropAccepted={handleDropAccepted}
                        value={[]}
                    />
                </Box>
            )}
        </div>
    );
});

AttachmentList.displayName = 'AttachmentList';
